import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { qk } from '@/lib/queryKeys';

export type NotificationCategory = {
  key: string;
  name: string;
  description: string | null;
  is_mandatory: boolean;
  default_enabled: boolean;
  module_key: string | null;
  sort_order: number;
};

export type QuietHours = {
  quiet_enabled: boolean;
  quiet_start: string;
  quiet_end: string;
};

export const DEFAULT_QUIET: QuietHours = {
  quiet_enabled: false,
  quiet_start: '22:00',
  quiet_end: '08:00',
};

const PREFS_KEY = ['my-notification-prefs'] as const;
const QUIET_KEY = ['my-quiet-hours'] as const;
const CATEGORIES_KEY = ['notification-categories'] as const;

export function useNotificationCategories() {
  return useQuery({
    queryKey: CATEGORIES_KEY,
    queryFn: async (): Promise<NotificationCategory[]> => {
      const { data, error } = await supabase
        .from('notification_categories')
        .select('key, name, description, is_mandatory, default_enabled, module_key, sort_order')
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as NotificationCategory[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** ההעדפות של העובד. אין שורה = ברירת המחדל של הסוג. */
export function useMyPrefs(employeeId: string | undefined) {
  return useQuery({
    queryKey: [...PREFS_KEY, employeeId],
    enabled: Boolean(employeeId),
    queryFn: async (): Promise<Record<string, boolean>> => {
      const { data, error } = await supabase
        .from('employee_notification_prefs')
        .select('category_key, push_enabled');
      if (error) throw error;
      return Object.fromEntries(
        (data ?? []).map((row) => [row.category_key as string, row.push_enabled as boolean]),
      );
    },
  });
}

export function useQuietHours(employeeId: string | undefined) {
  return useQuery({
    queryKey: [...QUIET_KEY, employeeId],
    enabled: Boolean(employeeId),
    queryFn: async (): Promise<QuietHours> => {
      const { data, error } = await supabase
        .from('employee_settings')
        .select('quiet_enabled, quiet_start, quiet_end')
        .maybeSingle();
      if (error) throw error;
      if (!data) return DEFAULT_QUIET;
      return {
        quiet_enabled: data.quiet_enabled as boolean,
        // Postgres מחזיר time כ HH:MM:SS, והקלט בדפדפן רוצה HH:MM
        quiet_start: String(data.quiet_start).slice(0, 5),
        quiet_end: String(data.quiet_end).slice(0, 5),
      };
    },
  });
}

export function useMeMutations(employeeId: string | undefined) {
  const queryClient = useQueryClient();

  const setPref = useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) => {
      if (!employeeId) throw new Error('no employee');
      // RLS דוחה כיבוי של סוג חובה, גם בקריאה ישירה ל API
      const { error } = await supabase
        .from('employee_notification_prefs')
        .upsert(
          // updated_at לא מתעדכן לבד: אין טריגר על הטבלה, ובלי זה
          // העמודה מראה את מועד היצירה ולא את מועד השינוי
          {
            employee_id: employeeId,
            category_key: key,
            push_enabled: enabled,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'employee_id,category_key' },
        );
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PREFS_KEY }),
  });

  const setQuiet = useMutation({
    mutationFn: async (values: QuietHours) => {
      if (!employeeId) throw new Error('no employee');
      const { error } = await supabase
        .from('employee_settings')
        .upsert(
          { employee_id: employeeId, ...values, updated_at: new Date().toISOString() },
          { onConflict: 'employee_id' },
        );
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUIET_KEY }),
  });

  /** העובד מעדכן רק שדות מותרים, דרך פונקציה ולא ישירות בטבלה. */
  const updateProfile = useMutation({
    mutationFn: async ({ email, avatarPath }: { email: string | null; avatarPath: string | null }) => {
      const { error } = await supabase.rpc('update_my_profile', {
        p_email: email,
        p_avatar_path: avatarPath,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.me }),
  });

  return { setPref, setQuiet, updateProfile };
}
